import React from 'react';
import { SectionHeading } from './primitives';
import { ROLE_VIEWS } from './profileConstants';

interface DashboardViewCardProps {
  currentViewRole: string;
  onSelectViewRole: (role: string) => void;
}

const DashboardViewCard: React.FC<DashboardViewCardProps> = ({ currentViewRole, onSelectViewRole }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8 dark:border-gray-700 dark:bg-gray-800">
    <SectionHeading
      icon={
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12s3.75-7.5 9.75-7.5S21.75 12 21.75 12s-3.75 7.5-9.75 7.5S2.25 12 2.25 12z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      }
    >
      Dashboard View
      <span className="ml-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
        Admin
      </span>
    </SectionHeading>
    <p className="-mt-2 mb-4 text-sm text-gray-500 dark:text-gray-400">
      Preview the app as a different role — this changes what each dashboard shows.
    </p>

    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {ROLE_VIEWS.map(rv => {
        const active = rv.value === currentViewRole;
        return (
          <button
            key={rv.value}
            type="button"
            onClick={() => onSelectViewRole(rv.value)}
            aria-pressed={active}
            className={`flex items-start gap-3 rounded-xl border p-4 text-left transition ${
              active
                ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500 dark:border-primary-400 dark:bg-primary-900/20 dark:ring-primary-400'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/40'
            }`}
          >
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white shadow-sm ${
                active ? 'bg-primary-600' : rv.tile
              }`}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12s3.75-7.5 9.75-7.5S21.75 12 21.75 12s-3.75 7.5-9.75 7.5S2.25 12 2.25 12z" />
                <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
              </svg>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{rv.label} View</span>
              <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400">{rv.description}</span>
              <span
                className={`mt-1 inline-block text-xs font-medium ${
                  active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {active ? 'Current view' : 'Click to view'}
              </span>
            </span>
            {active && (
              <svg className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                <path fillRule="evenodd" d="M16.7 6.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-3-3a1 1 0 111.4-1.4L9 12.6l6.3-6.3a1 1 0 011.4 0z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  </div>
);

export default DashboardViewCard;
